import { MessageCircle, Send } from 'lucide-react'
import { useState } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Textarea } from 'src/shared/components/ui/textarea'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { CardPosition } from '../types'

export const CanvasCommentDraft = ({
  position,
  scale,
  onSubmit,
  onCancel,
}: {
  position: CardPosition
  scale: number
  onSubmit: (text: string) => void
  onCancel: () => void
}) => {
  const { t } = useLocalization()
  const [text, setText] = useState('')

  const submit = () => {
    const trimmed = text.trim()
    if (!trimmed) return
    onSubmit(trimmed)
  }

  return (
    <div
      className="pointer-events-auto absolute left-0 top-0"
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        zIndex: 30,
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div style={{ transform: `scale(${1 / scale})`, transformOrigin: '0 0' }}>
        <div className="-translate-y-full">
          <div className="flex h-8 w-8 items-center justify-center rounded-full rounded-bl-none border-2 border-white bg-blue-600 text-white shadow-lg">
            <MessageCircle className="h-4 w-4" />
          </div>

          <Column className="absolute bottom-0 left-10 w-64 cursor-auto gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
            <Textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('Add a comment...', 'Lägg till en kommentar...')}
              className="min-h-16 resize-none border-0 p-1 text-xs shadow-none focus-visible:ring-0"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  submit()
                }
                if (e.key === 'Escape') {
                  e.stopPropagation()
                  onCancel()
                }
              }}
            />
            <Row className="justify-end gap-1">
              <Button variant="ghost" size="tiny" onClick={onCancel}>
                {t('Cancel', 'Avbryt')}
              </Button>
              <Button
                size="tiny"
                onClick={submit}
                disabled={!text.trim()}
                aria-label={t('Post comment', 'Publicera kommentar')}
              >
                <Send className="mr-1 h-3 w-3" />
                {t('Post', 'Publicera')}
              </Button>
            </Row>
          </Column>
        </div>
      </div>
    </div>
  )
}
